import { DUPE_API_URL } from '@dupe/common/constants'
import { getShopKey } from '@dupe/common/helpers'
import axios from 'axios'
import type { ParsedUrlQuery } from 'querystring'
import { getDomain } from 'tldts'
import type { ResultsResp } from '~/src/db/queryResultsResp'

export const createPublicUrl = (
  path: string,
  {
    excludeProtocol,
    publicUrlOverride,
    local,
  }: {
    excludeProtocol?: boolean
    publicUrlOverride?: string
    local?: boolean
  } = {}
): string => {
  const vercelUrl = process.env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL
    ? `https://${process.env.NEXT_PUBLIC_VERCEL_PROJECT_PRODUCTION_URL}`
    : undefined

  const url = `${(local
    ? ''
    : publicUrlOverride ||
      process.env.NEXT_PUBLIC_PUBLIC_URL ||
      vercelUrl ||
      'https://dupe.com'
  ).replace(/\/$/, '')}/${path.replace(/^\//, '')}`

  // exclude protocol if requested
  if (url && excludeProtocol) {
    return url.replace(/^https?:\/\//, '')
  }

  return url
}

export const getCurrencySymbolFromPrice = (
  price: string
): 'USD' | 'GBP' | 'EUR' => {
  // look for symbol in price e.g $ or £ or €
  const symbol = price.match(/[£$€]/)?.[0]
  if (symbol === '£') return 'GBP'
  if (symbol === '$') return 'USD'
  if (symbol === '€') return 'EUR'

  return 'USD'
}

export const currencyFormatter = (
  amount: number,
  {
    currency = 'USD',
    currencySymbol = '$',
    round,
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    locales = 'en-US',
  }: {
    currency?: string
    currencySymbol?: string
    round?: boolean
    minimumFractionDigits?: number
    maximumFractionDigits?: number
    locales?: string | string[]
  } = {}
) => {
  if (!amount) return ''

  const cleanAmount = parseFloat(`${amount}`.replace(/^\D+/g, ''))
  // if isNan revert back to preious value
  amount = isNaN(cleanAmount) ? amount : cleanAmount

  let currencyBasedOnCountry = currency

  switch (currencySymbol) {
    case '£':
    case 'GBP':
      currencyBasedOnCountry = 'GBP'
      break
    case '€':
    case 'EUR':
      currencyBasedOnCountry = 'EUR'
      break
    case '¥':
    case 'JPY':
      currencyBasedOnCountry = 'JPY'
      break
    case 'A':
    case 'A$':
    case 'AUD':
      currencyBasedOnCountry = 'AUD'
      break
    case '$':
    case 'USD':
    case 'US$':
      currencyBasedOnCountry = 'USD'
      break
    default:
      currencyBasedOnCountry = 'USD'
      break
  }

  const shouldUseParam =
    currency !== 'USD' && currencySymbol === '$' ? true : false

  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: shouldUseParam
      ? currency || currencyBasedOnCountry
      : currencyBasedOnCountry,
    minimumFractionDigits,
    maximumFractionDigits,
  }

  try {
    return new Intl.NumberFormat(locales, options).format(
      round ? Math.round(amount) : amount
    )
  } catch (e) {
    return new Intl.NumberFormat(locales, {
      ...options,
      currency: 'USD',
    }).format(round ? Math.round(amount) : amount)
  }
}

export const pctFormatter = (
  amount: number,
  {
    locales = 'en-US',
    minimumFractionDigits = 0,
  }: {
    locales?: string | string[]
    minimumFractionDigits?: number
  } = {}
) =>
  amount !== undefined
    ? new Intl.NumberFormat(locales, {
        style: 'percent',
        minimumFractionDigits,
      }).format(amount)
    : ''

export const formatNumberToK = (
  amount: number,
  {
    currency = 'USD',
    round,
    locales = 'en-US',
  }: {
    currency?: string
    round?: boolean
    locales?: string | string[]
  } = {}
): string => {
  // Check if the amount is in thousands and format it
  if (amount >= 1000) {
    const thousands = amount / 1000
    // Use the currencyFormatter for the formatted thousands value, ensuring consistent currency formatting
    // Since currencyFormatter expects a number, we round the thousands value to get an appropriate format
    return currencyFormatter(thousands, { currency, round, locales }) + 'k'
  } else {
    // Use the currencyFormatter for amounts less than 1000
    return currencyFormatter(amount, { currency, round, locales })
  }
}

/**
 * Check if a URL is valid
 * @param url - The URL to check
 * @returns The URL object if valid, false otherwise
 */
export const isValidUrl = (url: string | null | undefined): URL | boolean => {
  if (!url) return false

  try {
    // if url does not start with http or https, add https
    if (!url.startsWith('http:/') && !url.startsWith('https:/')) {
      url = `https://${url}`
    }

    // if url has invalid origin e.g https://.www. clean it up
    if (url.startsWith('https://.www.')) {
      url = url.replace('https://.www.', 'https://www.')
    }

    const urlObj = new URL(url)
    const isUrl =
      urlObj.hostname.indexOf('.') > -1 || urlObj.hostname === 'localhost'

    return isUrl ? urlObj : false
  } catch (e) {
    return false
  }
}

/**
 * Extract the first URL from the text
 * @param text - The text to extract the URL from
 * @returns The URL if found, null otherwise
 */
export const extractUrl = (text?: string): URL | boolean => {
  if (!text) return false

  const url = text.match(/https?:\/\/[^\s]+/)?.[0]
  return isValidUrl(url)
}

export const isDupeUrl = (
  url?: string,
  currentHostname?: string
): URL | boolean => {
  if (!url) return false

  const valudUrl = isValidUrl(url)
  const selfHostnames = ['localhost', 'dupe.com']

  if (currentHostname) {
    selfHostnames.push(currentHostname)
  }

  const dupeUrl =
    valudUrl instanceof URL && selfHostnames.includes(valudUrl.hostname)

  return dupeUrl ? valudUrl : false
}

export const getRedirectParams = (link: string, query: ParsedUrlQuery) => {
  const { src, hash, idx, pick, key } = query || {}
  const params = new URLSearchParams({
    link: link,
  })

  if (hash) {
    params.set('hash', hash as string)
  }

  if (pick) {
    params.set('pick', pick as string)
  }

  if (src) {
    params.set('src', src as string)
  }

  if (idx) {
    params.set('idx', idx as string)
  }

  if (key) {
    const domain = getDomain(key as string)
    if (domain) {
      params.set('key', domain)
    }
  }

  if (typeof window !== 'undefined') {
    const clickoutUrl = window.location.pathname + window.location.search
    params.set('clickout_url', clickoutUrl)
  }

  return params
}

export const convertToRedirectUrl = (
  url: string | null | undefined,
  params?: {
    source?: string
    index?: number | null
    pick?: boolean
    key?: string | null
    hash?: string | null
    google_product_id?: string | null
    source_shop?: string | null
    shopping_source?: string | null
    country?: string | null
    partner?: boolean
  },
  utmParams?: Record<string, string>
): string => {
  if (!url && !params?.google_product_id) return ''

  try {
    const searchParams = new URLSearchParams({
      link: url || params?.google_product_id || '',
    })

    if (params?.partner) {
      searchParams.set('partner', '1')
    }

    if (params?.hash) {
      searchParams.set('hash', params.hash)
    }

    if (params?.source) {
      searchParams.set('src', params.source)
    }

    if (params?.index) {
      searchParams.set('idx', `${params.index}`)
    }

    if (params?.pick) {
      searchParams.set('pick', `${params.pick}`)
    }

    if (params?.key) {
      const domain = getDomain(params?.key as string)
      if (domain) {
        searchParams.set('key', domain)
      }
    }

    // if google_product_id is present, add it to the search params
    if (params?.google_product_id) {
      searchParams.set('google_product_id', params.google_product_id)
    }

    if (params?.source_shop) {
      searchParams.set('source_shop', params.source_shop)
    }

    if (params?.shopping_source) {
      searchParams.set('shopping_source', params.shopping_source)
    }

    if (params?.country) {
      searchParams.set('selected_country', params.country)
    }

    // append UTM params
    Object.entries(utmParams || {}).forEach(([key, value]) => {
      searchParams.set(key, value)
    })

    return `/redirect?${searchParams.toString()}`
  } catch (e) {
    // use the original url
    return url || params?.google_product_id || ''
  }
}

export const cleanPhoneNumber = (phoneNumber: string): string => {
  const cleanNumber = phoneNumber.replace(/\D/g, '')
  const safeNumber = `+1${cleanNumber}`
  return safeNumber
}

export const fixUrl = (url?: string): string => {
  if (!url) return ''
  if (!isValidUrl(url)) return ''

  // if url starts with http:// or https://, remove the protocol
  if (url.startsWith('https://http://')) {
    return url.replace('https://http://', 'https://')
  }

  // if url starts with //, add https:// to the beginning
  if (url.startsWith('//')) {
    return `https:${url}`
  }

  return url || ''
}

export const extractPriceFromResult = (resultsData: ResultsResp | null) => {
  if (!resultsData) return { price: null, currency: 'USD' }

  const dataDetected = resultsData?.dataDetected || {}
  const dataDetectedPrice = dataDetected?.price
  const dataDetectedCurrency = dataDetected?.currency

  const pricing = resultsData?.pricing || {}
  const pricingPrice = pricing ? Number(pricing?.price) : undefined
  const pricingCurrency = pricing?.currency

  const price = pricingPrice || dataDetectedPrice
  const currency = pricingCurrency || dataDetectedCurrency

  return {
    price,
    currency,
  }
}

export const getNext2IoDomain = (url: string): string => {
  const NEXT2_IO_DOMAINS: string[] = ['target.com', 'amazon.com']

  // If not a next2.io URL, use the standard getDomain function
  if (!url.includes('next2.io')) {
    return getDomain(url) || ''
  }

  // Check for mapped domains in the URL
  for (const domain of NEXT2_IO_DOMAINS) {
    if (url.includes(domain)) {
      return domain
    }
  }

  // Default fallback to url domain
  return getDomain(url) || ''
}

export const getNext2IoShopKey = (url: string): string => {
  const NEXT2_IO_SHOP_KEYS: string[] = [
    'https://www.target.com',
    'https://www.amazon.com',
  ]

  // If not a next2.io URL, use the standard getShopKey function
  if (!url.includes('next2.io')) {
    return getShopKey(url)
  }

  // Check for mapped shop keys in the URL
  for (const shopKey of NEXT2_IO_SHOP_KEYS) {
    if (url.includes(shopKey)) {
      return shopKey
    }
  }

  // Default fallback to url domain
  return getShopKey(url)
}

/**
 * Estimates reading time in minutes based on word count, stripping HTML tags.
 * @param text The input text (can include HTML).
 * @param wpm Words per minute reading speed (default: 200).
 * @returns Estimated reading time in minutes.
 */
export function getReadingTime(text: string, wpm = 200): number {
  if (!text) return 0
  // Basic strip HTML tags
  const plainText = text.replace(/<[^>]*>/g, '')
  const words = plainText.trim().split(/\s+/).length
  return Math.ceil(words / wpm)
}

export function buildRedirectLink({
  link,
  query,
  utmParams = {},
  extensionState,
  uidOverride,
  fromMobileApp,
  mobileAppVersion,
  DUPE_API_URL,
}: {
  link: string
  query: any
  utmParams?: Record<string, string>
  extensionState?: { installed?: boolean; version?: string }
  uidOverride?: string
  fromMobileApp?: boolean
  mobileAppVersion?: string
  DUPE_API_URL: string
}): URL {
  const params = getRedirectParams(link, query)
  const redirectLink = new URL(`${DUPE_API_URL}/api/redirect?${params}`)

  // append UTM params
  Object.entries(utmParams || {}).forEach(([key, value]) => {
    redirectLink.searchParams.set(key, value)
  })

  // append extension info
  if (extensionState?.installed) {
    redirectLink.searchParams.set(
      'extension',
      JSON.stringify({
        installed: extensionState.installed || false,
        version: extensionState.version || '',
      })
    )
  }

  // append uidOverride
  if (uidOverride) {
    redirectLink.searchParams.set('uid-mobile', uidOverride)
  }

  // append mobile app info
  if (fromMobileApp) {
    redirectLink.searchParams.set('ref', 'mobile-app')
    redirectLink.searchParams.set('mobile-version', mobileAppVersion || '')
  }

  return redirectLink
}

export const getGoogleProductUrl = async (
  productId: string,
  source: string,
  googleSource: string,
  selectedCountry = 'us'
): Promise<string | null> => {
  try {
    const url = new URL(`${DUPE_API_URL}/api/google-product`)
    if (productId) url.searchParams.set('productId', productId)
    if (source) url.searchParams.set('sourceShop', source)
    if (googleSource) url.searchParams.set('googleSource', googleSource)
    if (selectedCountry) url.searchParams.set('country', selectedCountry)

    const resp = await axios.get(url.toString())
    const product = resp.data?.product || {}

    return product.link || null
  } catch (err) {
    return null
  }
}
